import { useState } from "react";
import { MapPin, Phone, MessageCircle, Clock, Navigation } from "lucide-react";
import { site, mapsDirectionsUrl, mapsEmbedUrl } from "@/config/site";
import { Section } from "./Section";
import { Reveal } from "./Reveal";

export const Location = () => {
    const [loadMap, setLoadMap] = useState(false);

    return (
        <Section id="location" index="14" label="Find Us" title="Visit the studio." alt>
            <div className="mt-14 grid gap-10 lg:grid-cols-2">
                <Reveal delay={0.05}>
                    <div className="space-y-8">
                        <div>
                            <p className="flex items-center gap-3 font-display text-[11px] uppercase tracking-[0.22em] text-rz-smoke">
                                <MapPin size={14} className="text-rz-red" /> Address
                            </p>
                            <address className="mt-3 text-base not-italic leading-relaxed text-rz-white">
                                {site.address.lines.map((l) => (
                                    <span key={l} className="block">
                                        {l}
                                    </span>
                                ))}
                            </address>
                        </div>
                        <div className="grid gap-6 sm:grid-cols-2">
                            <div>
                                <p className="flex items-center gap-3 font-display text-[11px] uppercase tracking-[0.22em] text-rz-smoke">
                                    <Phone size={14} className="text-rz-red" /> Call
                                </p>
                                <a
                                    data-testid="location-phone"
                                    href={`tel:${site.phone}`}
                                    className="mt-3 block text-base text-rz-white transition-colors hover:text-rz-red"
                                >
                                    {site.phoneDisplay}
                                </a>
                                <a
                                    data-testid="location-phone-secondary"
                                    href={`tel:${site.phoneSecondary}`}
                                    className="mt-1 block text-base text-rz-white transition-colors hover:text-rz-red"
                                >
                                    {site.phoneSecondaryDisplay}
                                </a>
                            </div>
                            <div>
                                <p className="flex items-center gap-3 font-display text-[11px] uppercase tracking-[0.22em] text-rz-smoke">
                                    <MessageCircle size={14} className="text-rz-red" /> WhatsApp
                                </p>
                                <a
                                    data-testid="location-whatsapp"
                                    href={site.whatsappLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-3 block text-base text-rz-white transition-colors hover:text-rz-red"
                                >
                                    Message the studio
                                </a>
                            </div>
                        </div>
                        <div>
                            <p className="flex items-center gap-3 font-display text-[11px] uppercase tracking-[0.22em] text-rz-smoke">
                                <Clock size={14} className="text-rz-red" /> Hours
                            </p>
                            <dl className="mt-3 space-y-1.5">
                                {site.hours.map((h) => (
                                    <div key={h.days} className="flex justify-between gap-6 text-base">
                                        <dt className="text-rz-smoke">{h.days}</dt>
                                        <dd className="text-rz-white">{h.time}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                        <div className="flex flex-wrap gap-4 pt-2">
                            <a
                                data-testid="location-directions"
                                href={mapsDirectionsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-sweep inline-flex items-center gap-2 bg-rz-red px-7 py-3.5 font-display text-[12px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-rz-reddeep"
                            >
                                <Navigation size={14} /> Get Directions
                            </a>
                            <a
                                data-testid="location-contact"
                                href="#contact"
                                className="inline-flex items-center border border-rz-white/40 px-7 py-3.5 font-display text-[12px] font-semibold uppercase tracking-[0.18em] text-rz-white transition-colors duration-300 hover:border-rz-white"
                            >
                                Contact Us
                            </a>
                            <a
                                data-testid="location-google-profile"
                                href={site.googleProfile}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-1 py-3.5 font-display text-[12px] font-semibold uppercase tracking-[0.18em] text-rz-smoke transition-colors duration-300 hover:text-rz-white"
                            >
                                Google Profile
                            </a>
                        </div>
                    </div>
                </Reveal>
                <Reveal delay={0.12}>
                    <div className="relative h-full min-h-[380px] border border-rz-steel/60">
                        {loadMap ? (
                            <iframe
                                title="Map — Revautoz studio, Colvale, Mapusa, Goa"
                                src={mapsEmbedUrl}
                                loading="lazy"
                                className="h-full min-h-[380px] w-full grayscale invert-[0.92] contrast-[0.9]"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        ) : (
                            <button
                                data-testid="map-load-button"
                                onClick={() => setLoadMap(true)}
                                className="group flex h-full min-h-[380px] w-full flex-col items-center justify-center gap-4 bg-rz-graphite/50 transition-colors hover:bg-rz-graphite"
                            >
                                <MapPin size={28} className="text-rz-red" />
                                <span className="font-display text-[12px] uppercase tracking-[0.22em] text-rz-smoke transition-colors group-hover:text-rz-white">
                                    Load interactive map
                                </span>
                                <span className="max-w-[32ch] text-center text-sm text-rz-smoke">
                                    Mushirwada, Colvale — near Picnic Garden and Binani
                                </span>
                            </button>
                        )}
                    </div>
                </Reveal>
            </div>
        </Section>
    );
};
